"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { Input } from "@/app/_module/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import {
  FEATURED,
  ROLE_CATEGORIES,
  brandFontCssUrls,
  brandFontStack,
  catalogFontStack,
  ensureFontCss,
  loadFontCatalog,
  previewCssUrl,
  toBrandFont,
  type BrandFont,
  type BrandFontRole,
  type CatalogFont,
  type FontCategory,
} from "@/app/_module/lib/brandFonts";

const STYLE_LABEL: Record<FontCategory, string> = {
  "sans-serif": "Sans",
  serif: "Serif",
  display: "Display",
  handwriting: "Script",
  monospace: "Mono",
};
// Plenty to scroll through; typing narrows the rest.
const MAX_ROWS = 150;

/** Search about 2,000 open-source fonts. Each name shows in its own font, loaded as its row scrolls into view. */
export function FontPicker({
  id,
  kind,
  value,
  onChange,
}: {
  id: string;
  /** Which font this picks; named kind because role is an ARIA attribute. */
  kind: BrandFontRole;
  value: BrandFont | null;
  onChange: (font: BrandFont | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [style, setStyle] = useState<FontCategory | "all">("all");
  const [catalog, setCatalog] = useState<CatalogFont[] | null>(null);
  const [failed, setFailed] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || catalog) return;
    setFailed(false);
    loadFontCatalog().then(setCatalog, () => setFailed(true));
  }, [open, catalog]);

  // The button shows the chosen font in itself.
  useEffect(() => {
    if (value) ensureFontCss(brandFontCssUrls(value)[0]);
  }, [value]);

  const { rows, total } = useMemo(() => {
    if (!catalog) return { rows: [] as CatalogFont[], total: 0 };
    const pool = catalog.filter(
      (f) =>
        ROLE_CATEGORIES[kind].includes(f.c) &&
        (kind === "heading" || f.w.includes(400)) &&
        (style === "all" || f.c === style),
    );
    const q = query.trim().toLowerCase();
    if (!q && style === "all") {
      const byId = new Map(pool.map((f) => [f.i, f]));
      return {
        rows: FEATURED[kind].map((i) => byId.get(i)).filter((f): f is CatalogFont => !!f),
        total: pool.length,
      };
    }
    const hits = q ? pool.filter((f) => f.f.toLowerCase().includes(q)) : pool;
    if (q) hits.sort((a, b) => Number(!a.f.toLowerCase().startsWith(q)) - Number(!b.f.toLowerCase().startsWith(q)));
    return { rows: hits.slice(0, MAX_ROWS), total: hits.length };
  }, [catalog, kind, style, query]);

  useEffect(() => {
    const root = listRef.current;
    if (!root || !rows.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const href = (entry.target as HTMLElement).dataset.css;
          if (href) ensureFontCss(href);
          io.unobserve(entry.target);
        }),
      { root, rootMargin: "120px" },
    );
    root.querySelectorAll<HTMLElement>("[data-css]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rows]);

  const pick = (font: BrandFont | null) => {
    onChange(font);
    setOpen(false);
  };
  const browsing = !query.trim() && style === "all";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className="flex h-10 w-full max-w-xs items-center justify-between gap-2 rounded-md border bg-background px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ fontFamily: value ? brandFontStack(value) : undefined }}
        >
          <span className="truncate">{value ? value.family : "Default"}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      {/* margin 0: globals.css pushes every [role=dialog] 5vh down on phones. */}
      <PopoverContent align="start" collisionPadding={8} className="w-[min(92vw,22rem)] p-0" style={{ margin: 0 }}>
        <div className="space-y-2 border-b p-3">
          {/* Radix focuses this on open. */}
          <Input
            aria-label="Search fonts"
            placeholder={catalog ? `Search ${total.toLocaleString()} fonts` : "Search fonts"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9"
          />
          <div className="flex flex-wrap gap-1" role="group" aria-label="Style">
            {(["all", ...ROLE_CATEGORIES[kind]] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={style === s}
                onClick={() => setStyle(s)}
                className={`rounded-full border px-2.5 py-0.5 text-xs ${
                  style === s ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {s === "all" ? "All" : STYLE_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        <div ref={listRef} role="listbox" aria-label="Fonts" className="max-h-72 overflow-y-auto p-1">
          {failed ? (
            <p className="p-3 text-sm text-muted-foreground">Couldn&apos;t load the font list. Close this and try again.</p>
          ) : !catalog ? (
            <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading fonts…
            </p>
          ) : (
            <>
              {browsing && (
                <button
                  type="button"
                  role="option"
                  aria-selected={!value}
                  onClick={() => pick(null)}
                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-base hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  Default
                  {!value && <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                </button>
              )}
              {rows.map((f) => (
                <button
                  key={f.i}
                  type="button"
                  role="option"
                  aria-selected={value?.id === f.i}
                  data-css={previewCssUrl(f)}
                  onClick={() => pick(toBrandFont(f, kind))}
                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="truncate text-base" style={{ fontFamily: catalogFontStack(f) }}>
                    {f.f}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                    {STYLE_LABEL[f.c]}
                    {value?.id === f.i && <Check className="h-3.5 w-3.5" />}
                  </span>
                </button>
              ))}
              {!rows.length && <p className="p-3 text-sm text-muted-foreground">No fonts match &ldquo;{query.trim()}&rdquo;.</p>}
            </>
          )}
        </div>
        {catalog && (
          <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
            {browsing
              ? `Popular picks. Search or choose a style to see all ${total.toLocaleString()}.`
              : total > rows.length
                ? `Showing ${rows.length} of ${total.toLocaleString()}. Keep typing to narrow it down.`
                : `${total.toLocaleString()} ${total === 1 ? "font" : "fonts"}.`}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
